// The meeting note of a tension: one active per (tension, meeting), enforced
// by the partial unique index `thread_activity_active_unique`.
export interface MeetingNoteStore {
  create(notes: string): Promise<string | undefined>
  update(id: string, notes: string): Promise<void>
  findActive(): Promise<string | undefined>
}

const isUniqueViolation = (error: unknown) =>
  String((error as Error)?.message).includes('thread_activity_active_unique')

// Saves the notes of one tension in one meeting (#197). The subscription
// returns a created note late: until then, every save used to create it again
// and was refused. Saves now run one after the other and reuse the created id;
// a note created meanwhile by another session is found and updated.
export function createMeetingNoteSaver(store: MeetingNoteStore) {
  let knownId: string | undefined
  let queue: Promise<unknown> = Promise.resolve()

  async function save(activityId: string | undefined, notes: string) {
    const id = activityId ?? knownId
    if (id) return store.update(id, notes)
    try {
      knownId = await store.create(notes)
    } catch (error) {
      if (!isUniqueViolation(error)) throw error
      knownId = await store.findActive()
      if (!knownId) throw error
      await store.update(knownId, notes)
    }
  }

  return (activityId: string | undefined, notes: string): Promise<void> => {
    const saved = queue.then(() => save(activityId, notes))
    queue = saved.catch(() => {})
    return saved
  }
}
